import React, { lazy, Suspense, useState, useEffect, useRef, useCallback } from 'react';
import LandingPage from './components/LandingPage';
import AuthModal from './components/AuthModal';
import PricingModal from './components/PricingModal';
import Dashboard from './components/Dashboard';
import SyntheticDashboard from './components/SyntheticDashboard';
import AddWebsiteModal from './components/AddWebsiteModal';
import EditWebsiteModal from './components/EditWebsiteModal';
import AddSyntheticModal from './components/AddSyntheticModal';
import EditSyntheticModal from './components/EditSyntheticModal';
import SyntheticResultsModal from './components/SyntheticResultsModal';
import ResetPasswordPage from './components/ResetPasswordPage';
import TelegramSettingsModal from './components/TelegramSettingsModal';
import LoadingSpinner from './components/LoadingSpinner';
import { 
  Plus, Activity, Globe, RefreshCw, Zap, TrendingUp, Shield, BarChart3, 
  User, LogOut, CreditCard, Menu, X, Server, Play, Pause, CheckCircle, XCircle, Clock,
  Send, HelpCircle, ScanLine, Sun, Moon, Wifi, WifiOff, ShieldAlert, 
  Cpu, Sparkles, Bell, Award, Star, Flame, Gauge, Radar
} from 'lucide-react';
import axios from 'axios';

const Admin = lazy(() => import('./pages/Admin'));
const AnalyticsDashboard = lazy(() => import('./components/AnalyticsDashboard'));
const ScanDashboard = lazy(() => import('./components/ScanDashboard'));

const API_BASE = "/api";

const capitalizeName = (name) => {
  if (!name) return '';
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

function App() {
  const greenColor = '#95DE1A';
  const redColor = '#FD0E0B';
  const orangeColor = '#F97316';
  const blueColor = '#0b7dc3';
  const purpleColor = '#8B5CF6';
  const pinkColor = '#EC4899';
  const gradientGreenStart = '#85C34A';
  const gradientGreenMiddle = '#95DE1A';
  const gradientGreenEnd = '#A5E84A';

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showTelegramModal, setShowTelegramModal] = useState(false);
  const [authMode, setAuthMode] = useState('signin');
  const [currentView, setCurrentView] = useState(() => {
    const hasSavedAdminView = new URLSearchParams(window.location.search).has('adminTab');
    return hasSavedAdminView && localStorage.getItem('role') === 'ADMIN' ? 'admin' : 'dashboard';
  });
  const [websites, setWebsites] = useState([]);
  const [activeWebsiteCount, setActiveWebsiteCount] = useState(0);
  const [websiteTotalCount, setWebsiteTotalCount] = useState(0);
  const [websiteStatusCounts, setWebsiteStatusCounts] = useState({ up: 0, down: 0, unknown: 0, inactive: 0, service: 0 });
  const [blacklistTotalCount, setBlacklistTotalCount] = useState(0);
  const [activeBlacklistCount, setActiveBlacklistCount] = useState(0);
  const [syntheticTests, setSyntheticTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [websitesLoading, setWebsitesLoading] = useState(true);
  const [syntheticLoading, setSyntheticLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState(null);
  const [showAddSyntheticModal, setShowAddSyntheticModal] = useState(false);
  const [editingSynthetic, setEditingSynthetic] = useState(null);
  const [viewingResults, setViewingResults] = useState(null);
  const [error, setError] = useState(null);
  const [resetToken, setResetToken] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('darkMode');
    return saved !== null ? saved === 'true' : true;
  });
  const [showScanProModal, setShowScanProModal] = useState(false);
  const menuRef = useRef();

  const [syntheticRefreshKey, setSyntheticRefreshKey] = useState(0);
  const [analyticsRefreshKey, setAnalyticsRefreshKey] = useState(0);
  const [adminRefreshKey, setAdminRefreshKey] = useState(0);
  const [scanRefreshKey, setScanRefreshKey] = useState(0);
  const [dashboardSearchTerm, setDashboardSearchTerm] = useState('');
  const [dashboardSearchApplied, setDashboardSearchApplied] = useState('');
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [syntheticFilter, setSyntheticFilter] = useState('all');
  const [scanTarget, setScanTarget] = useState(null);

  const analyticsTimeoutRef = useRef(null);
  const adminTimeoutRef = useRef(null);
  const websiteRequestRef = useRef(null);
  const blacklistRequestRef = useRef(null);
  const dashboardGenerationRef = useRef(0);
  const dashboardSearchTimeoutRef = useRef(null);
  
  const isProUser = currentUser?.planType?.toLowerCase() === 'professional' || currentUser?.planType?.toLowerCase() === 'enterprise';
  const isAdmin = localStorage.getItem("role") === "ADMIN";
  
  useEffect(() => {
    localStorage.setItem('darkMode', darkMode);
    document.documentElement.classList.toggle('dark', darkMode);
  }, [darkMode]);

  useEffect(() => {
    const handleOpenPricing = () => {
      setShowPricingModal(true);
    };
    window.addEventListener('openPricingModal', handleOpenPricing);
    return () => {
      window.removeEventListener('openPricingModal', handleOpenPricing);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const res = await axios(`${API_BASE}/user/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const userData = {
        ...res.data,
        maxWebsites: res.data.maxWebsites,
        maxPortsPerWebsite: res.data.maxPortsPerWebsite,
        maxApisPerWebsite: res.data.maxApisPerWebsite,
        maxSyntheticTests: res.data.maxSyntheticTests || 1,
      };
      setCurrentUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));
      setIsAuthenticated(true);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.removeItem("token");
        localStorage.removeItem("user");
        setIsAuthenticated(false);
        setCurrentUser(null);
      }
      console.error('Error fetching user data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const resetTokenFromUrl = urlParams.get('token');
    
    if (resetTokenFromUrl) {
      setResetToken(resetTokenFromUrl);
      setLoading(false);
      return;
    }

    const authToken = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    
    if (authToken && user) {
      setIsAuthenticated(true);
      setCurrentUser(JSON.parse(user));
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const fetchBlacklistMonitors = async () => {
    if (blacklistRequestRef.current) return blacklistRequestRef.current.promise;
    const generation = dashboardGenerationRef.current;
    const controller = new AbortController();
    const request = { controller, promise: null };
    request.promise = (async () => {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/blacklist-monitors`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      const summaryRes = await axios.get(`${API_BASE}/blacklist-monitors/summary`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      if (generation !== dashboardGenerationRef.current) return;
      const rows = Array.isArray(res.data) ? res.data : res.data.items;
      const blacklistAsWebsites = rows.map(b => ({
        id: b.id,
        name: b.name,
        target: b.target,
        url: '',
        type: 'BLACKLIST',
        status: b.lastStatus ? (Object.values(b.lastStatus).some(v => v === true) ? 'listed' : 'clean') : 'unknown',
        isActive: b.isActive,
        lastChecked: b.lastChecked,
        lastStatus: b.lastStatus,
        notifyEmail: b.notifyEmail,
        notifyTelegram: b.notifyTelegram,
        interval: b.interval,
        uptimeChecks: [],
        portMonitors: [],
        apiMonitors: [],
        pingMonitors: [],
        uptimePercentage: null,
        downtimeCount: 0,
        responseTime: null,
      }));

      setWebsites(prev => [...prev.filter(w => w.type !== 'BLACKLIST'), ...blacklistAsWebsites]);
      const summary = summaryRes.data;
      setBlacklistTotalCount(summary.total);
      setActiveBlacklistCount(summary.activeTotal);
    })();
    blacklistRequestRef.current = request;
    try {
      return await request.promise;
    } catch (err) {
      if (generation === dashboardGenerationRef.current && err.name !== 'CanceledError' && err.name !== 'AbortError') {
        console.error('Error fetching blacklist monitors:', err);
        setError('Failed to fetch blacklist monitors. Please retry.');
      }
    } finally {
      if (blacklistRequestRef.current === request) blacklistRequestRef.current = null;
    }
  };

  const fetchWebsites = async (options = {}) => {
    const { showRefreshIndicator = false, background = false } = options;
    if (websiteRequestRef.current) return websiteRequestRef.current.promise;
    if (showRefreshIndicator) setRefreshing(true);
    else if (!background) setWebsitesLoading(true);
    setError(null);
    const generation = dashboardGenerationRef.current;
    const controller = new AbortController();
    const request = { controller, promise: null };
    request.promise = (async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/websites`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      if (!response.ok) {
        if (response.status === 401) {
          handleLogout();
          return null;
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const rows = await response.json();
      const summaryResponse = await fetch(`${API_BASE}/websites/summary`, {
        headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
      });
      if (!summaryResponse.ok) throw new Error(`HTTP error! status: ${summaryResponse.status}`);
      const summary = await summaryResponse.json();
      if (generation !== dashboardGenerationRef.current) return null;
      setWebsites(rows.filter(w => w.type !== 'BLACKLIST'));
      setActiveWebsiteCount(summary.activeTotal);
      setWebsiteTotalCount(summary.total);
      setWebsiteStatusCounts(summary.statusCounts);
      await fetchBlacklistMonitors();
      return rows;
    })();
    websiteRequestRef.current = request;
    try {
      await request.promise;
    } catch (err) {
      if (generation === dashboardGenerationRef.current && err.name !== 'AbortError') {
        console.error('Error fetching websites:', err);
        setError('Failed to fetch websites. Please check if the server is running.');
      }
    } finally {
      if (websiteRequestRef.current === request) websiteRequestRef.current = null;
      if (generation === dashboardGenerationRef.current) {
        if (!background) setWebsitesLoading(false);
        if (showRefreshIndicator) setRefreshing(false);
      }
    }
  };

  const fetchSyntheticTests = async (options = {}) => {
    const { showRefreshIndicator = false, background = false } = options;
    try {
      if (showRefreshIndicator) setRefreshing(true);
      if (!background) setSyntheticLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/synthetic-tests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSyntheticTests(res.data);
      setSyntheticRefreshKey(prev => prev + 1);
    } catch (err) {
      console.error('Error fetching synthetic tests:', err);
      setError('Failed to fetch synthetic tests.');
    } finally {
      if (!background) setSyntheticLoading(false);
      if (showRefreshIndicator) setRefreshing(false);
    }
  };

  const handleAddBlacklist = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/blacklist-monitors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: data.name,
          target: data.target,
          type: data.type,
          interval: data.interval,
          notifyEmail: data.notifyEmail,
          notifyTelegram: data.notifyTelegram
        })
      });
      const resData = await response.json();
      if (response.ok) {
        await fetchWebsites({ background: false });
        return { success: true };
      } else {
        return { success: false, error: resData.error || 'Failed to add blacklist monitor' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleUpdateBlacklist = async (id, data) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/blacklist-monitors/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: data.name,
          target: data.blacklistTarget,
          type: data.blacklistType,
          interval: data.blacklistInterval,
          isActive: data.isActive,
          notifyEmail: data.notifyEmail,
          notifyTelegram: data.notifyTelegram
        })
      });
      if (response.ok) {
        await fetchWebsites({ background: false });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleAddWebsite = async (data) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/websites`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      const resData = await response.json();
      if (response.ok) {
        await fetchWebsites({ background: false });
        return { success: true };
      } else {
        return { success: false, error: resData.error || 'Failed' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleEditWebsite = async (id, data) => {
    try {
      const token = localStorage.getItem('token');
      const website = websites.find(w => w.id === id);
      if (website && website.type === 'BLACKLIST') {
        return await handleUpdateBlacklist(id, data);
      }
      const response = await fetch(`${API_BASE}/websites/${id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });
      if (response.ok) {
        await fetchWebsites({ background: false });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || error.details || 'Unknown error' };
      }
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const handleDeleteWebsite = async (id) => {
    if (!window.confirm('Are you sure you want to delete this monitor?')) return;
    try {
      const token = localStorage.getItem('token');
      const website = websites.find(w => w.id === id);
      const endpoint = website && website.type === 'BLACKLIST' 
        ? `${API_BASE}/blacklist-monitors/${id}`
        : `${API_BASE}/websites/${id}`;
      await fetch(endpoint, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchWebsites({ background: true });
    } catch (err) {
      alert('Failed to delete');
    }
  };

  const handleToggleSynthetic = async (testId, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      await axios.patch(
        `${API_BASE}/synthetic-tests/${testId}`,
        { isActive: !currentActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchSyntheticTests({ background: false });
    } catch (err) {
      console.error('Failed to toggle test status:', err);
      alert('Failed to update test status. Please try again.');
    }
  };

  const handleToggleWebsite = async (websiteId, currentActive) => {
    try {
      const token = localStorage.getItem('token');
      const website = websites.find(w => w.id === websiteId);
      if (!website) return;

      if (!currentActive) {
        const activeCount = activeWebsiteCount;
        const maxActive = currentUser?.maxWebsites || 3;
        
        if (activeCount >= maxActive && website.type !== 'BLACKLIST') {
          alert(`You have reached the maximum number of active websites (${maxActive}) allowed by your ${currentUser?.planType} plan. Please deactivate another website first or upgrade your plan.`);
          return;
        }
      }

      const endpoint = website.type === 'BLACKLIST'
        ? `${API_BASE}/blacklist-monitors/${websiteId}`
        : `${API_BASE}/websites/${websiteId}`;

      const response = await fetch(endpoint, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isActive: !currentActive })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update status');
      }

      await fetchWebsites({ background: true });
    } catch (err) {
      console.error('Error toggling status:', err);
      alert(err.message || 'Failed to update status. Please try again.');
    }
  };

  const refreshAnalytics = () => {
    if (analyticsTimeoutRef.current) clearTimeout(analyticsTimeoutRef.current);
    setRefreshing(true);
    setAnalyticsRefreshKey(prev => prev + 1);
    analyticsTimeoutRef.current = setTimeout(() => setRefreshing(false), 2000);
  };

  const refreshAdmin = () => {
    if (adminTimeoutRef.current) clearTimeout(adminTimeoutRef.current);
    setRefreshing(true);
    setAdminRefreshKey(prev => prev + 1);
    adminTimeoutRef.current = setTimeout(() => setRefreshing(false), 2000);
  };

  const refreshScan = () => {
    setRefreshing(true);
    setScanRefreshKey(prev => prev + 1);
    setTimeout(() => setRefreshing(false), 1000);
  };

  const handleRefresh = () => {
    switch (currentView) {
      case 'dashboard':
        fetchWebsites({ showRefreshIndicator: true, background: false });
        break;
      case 'synthetic':
        fetchSyntheticTests({ showRefreshIndicator: true, background: false });
        break;
      case 'analytics':
        refreshAnalytics();
        break;
      case 'admin':
        refreshAdmin();
        break;
      case 'scan':
        refreshScan();
        break;
      default:
        break;
    }
  };

  const handleAddSynthetic = async (testData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/synthetic-tests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testData)
      });

      const data = await response.json();
      
      if (response.ok) {
        await fetchSyntheticTests({ background: false });
        return { success: true };
      } else {
        return { success: false, error: data.error || data.message || 'Failed to add test' };
      }
    } catch (error) {
      console.error('Error adding synthetic test:', error);
      return { success: false, error: 'Failed to add test' };
    }
  };

  const handleEditSynthetic = async (id, testData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/synthetic-tests/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(testData)
      });

      if (response.ok) {
        await fetchSyntheticTests({ background: false });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.error || 'Failed to update test' };
      }
    } catch (error) {
      console.error('Error updating synthetic test:', error);
      return { success: false, error: 'Failed to update test' };
    }
  };

  const handleDeleteSynthetic = async (id) => {
    if (!window.confirm('Are you sure you want to delete this test? This will also delete all results.')) return;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/synthetic-tests/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchSyntheticTests({ background: false });
      } else {
        alert('Failed to delete test');
      }
    } catch (error) {
      console.error('Error deleting synthetic test:', error);
      alert('Failed to delete test');
    }
  };

  const handleRunSynthetic = async (id) => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_BASE}/synthetic-tests/${id}/run`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Error running test:', error);
      alert('Failed to run test');
    }
  };

  const handleAuthSuccess = async (userData) => {
    let token = null;
    let user = null;

    if (userData.token) {
      token = userData.token;
    } else if (userData.data?.token) {
      token = userData.data.token;
    }

    if (userData.user) {
      user = userData.user;
    } else if (userData.data?.user) {
      user = userData.data.user;
    }

    if (token) localStorage.setItem('token', token);
    if (user) {
      setCurrentUser(user);
      localStorage.setItem('user', JSON.stringify(user));
    }

    setIsAuthenticated(true);
    setShowAuthModal(false);
    setLoading(true);
    await fetchUser();
    await fetchWebsites({ background: false });
    await fetchSyntheticTests({ background: false });
    setLoading(false);
  };

  const handleLogout = () => {
    dashboardGenerationRef.current += 1;
    websiteRequestRef.current?.controller.abort();
    blacklistRequestRef.current?.controller.abort();
    websiteRequestRef.current = null;
    blacklistRequestRef.current = null;
    setActiveWebsiteCount(0);
    setWebsiteTotalCount(0);
    setWebsiteStatusCounts({ up: 0, down: 0, unknown: 0, inactive: 0, service: 0 });
    setBlacklistTotalCount(0);
    setActiveBlacklistCount(0);
    setDashboardSearchTerm('');
    setDashboardSearchApplied('');
    setDashboardFilter('all');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setCurrentUser(null);
    setWebsites([]);
    setSyntheticTests([]);
    setCurrentView('dashboard');
    setMobileMenuOpen(false);
  };

  const handleGetStarted = () => {
    setAuthMode('signup');
    setShowAuthModal(true);
  };

  const handleSignIn = () => {
    setAuthMode('signin');
    setShowAuthModal(true);
  };

  const handlePlanChange = async (newSubscription) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/user/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const userData = {
        ...res.data,
        maxWebsites: res.data.maxWebsites,
        maxPortsPerWebsite: res.data.maxPortsPerWebsite,
        maxApisPerWebsite: res.data.maxApisPerWebsite,
        maxSyntheticTests: res.data.maxSyntheticTests || 1,
      };
      setCurrentUser(userData);
      localStorage.setItem('user', JSON.stringify(userData));

      await fetchWebsites({ background: false });
      await fetchSyntheticTests({ background: false });

    } catch (error) {
      console.error('Error fetching updated user data:', error);
      setCurrentUser(prev => ({
        ...prev,
        ...newSubscription
      }));
      localStorage.setItem('user', JSON.stringify({ ...currentUser, ...newSubscription }));
      await fetchWebsites({ background: false });
      await fetchSyntheticTests({ background: false });
    }
  };

  const handleResetPasswordSuccess = () => {
    setResetToken(null);
    setAuthMode('signin');
    setShowAuthModal(true);
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const canAddWebsite = () => {
    if (!currentUser) return false;
    const allowedStatuses = ['active', 'trial'];
    if (!allowedStatuses.includes(currentUser.subscriptionStatus)) return false;
    if (currentUser.planType !== 'free' && currentUser.planEndDate && new Date() > new Date(currentUser.planEndDate)) return false;
    const activeCount = activeWebsiteCount;
    return activeCount < (currentUser.maxWebsites || 3);
  };

  const canActivateWebsite = (website) => {
    if (!currentUser) return false;
    const activeCount = activeWebsiteCount;
    const maxActive = currentUser.maxWebsites || 3;
    if (website.isActive) return true;
    return activeCount < maxActive;
  };

  const canAddSynthetic = () => {
    if (syntheticLoading) return false;
    if (!currentUser) return false;
    const allowedStatuses = ['active', 'trial'];
    if (!allowedStatuses.includes(currentUser.subscriptionStatus)) return false;
    if (currentUser.planType !== 'free' && currentUser.planEndDate && new Date() > new Date(currentUser.planEndDate)) return false;
    const activeCount = syntheticTests.filter(t => t.isActive).length;
    return activeCount < (currentUser.maxSyntheticTests || 1);
  };

  const handleScanClick = () => {
    if (!isProUser) {
      setShowScanProModal(true);
      return;
    }
    setCurrentView('scan');
  };

  const getMaxInterval = (planType) => {
    switch (planType?.toLowerCase()) {
      case 'free': return 300;
      case 'starter': return 60;
      case 'professional': return 30;
      case 'enterprise': return 15;
      default: return 300;
    }
  };
  const maxIntervalCheck = currentUser ? getMaxInterval(currentUser.planType) : 300;
  const hasAIFeatures = currentUser ? ['professional', 'enterprise'].includes(currentUser.planType?.toLowerCase()) : false;

  const handleScanWebsite = (website) => {
    if (!isProUser) {
      setShowScanProModal(true);
      return;
    }
    const target = website.url || website.target;
    const type = website.type === 'BLACKLIST' 
      ? (target?.match(/^\d+\.\d+\.\d+\.\d+$/) ? 'ip' : 'domain')
      : 'domain';
    setScanTarget({ target, type, name: website.name });
    setCurrentView('scan');
  };

  const upWebsites = websiteStatusCounts.up;
  const downWebsites = websiteStatusCounts.down;
  const unknownWebsites = websiteStatusCounts.unknown;
  const serviceWebsites = websiteStatusCounts.service;
  const inactiveWebsites = websiteStatusCounts.inactive + blacklistTotalCount - activeBlacklistCount;
  const blacklistWebsites = blacklistTotalCount;
  
  const syntheticTotal = syntheticTests.length;
  const syntheticSuccess = syntheticTests.filter(t => t.isActive && t.results?.[0]?.status === 'success').length;
  const syntheticFailed = syntheticTests.filter(t => t.isActive && t.results?.[0]?.status === 'failed').length;
  const syntheticInactive = syntheticTests.filter(t => !t.isActive).length;

  useEffect(() => {
    if (isAuthenticated) {
      if (currentView === 'dashboard') {
        fetchWebsites({ background: false });
      } else if (currentView === 'synthetic') {
        fetchSyntheticTests({ background: false });
      }
    }
  }, [isAuthenticated, currentView]);

  useEffect(() => {
    if (!isAuthenticated || currentView !== 'dashboard') return;
    dashboardSearchTimeoutRef.current = setTimeout(() => {
      dashboardSearchTimeoutRef.current = null;
      setDashboardSearchApplied(dashboardSearchTerm);
    }, 350);
    return () => {
      clearTimeout(dashboardSearchTimeoutRef.current);
      dashboardSearchTimeoutRef.current = null;
    };
  }, [dashboardSearchTerm, currentView, isAuthenticated]);

  const submitDashboardSearch = () => {
    clearTimeout(dashboardSearchTimeoutRef.current);
    dashboardSearchTimeoutRef.current = null;
    setDashboardSearchApplied(dashboardSearchTerm);
  };

  useEffect(() => {
    if (!isAuthenticated) return;
    const interval = setInterval(() => {
      if (currentView === 'dashboard') {
        if (websiteRequestRef.current || blacklistRequestRef.current) return;
        fetchWebsites({ background: true });
      } else if (currentView === 'synthetic') {
        fetchSyntheticTests({ background: true });
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [currentView, isAuthenticated]);

  if (resetToken) {
    return (
      <ResetPasswordPage 
        token={resetToken}
        onSuccess={handleResetPasswordSuccess}
        onCancel={() => {
          setResetToken(null);
          window.history.replaceState({}, document.title, window.location.pathname);
        }}
        darkMode={darkMode}
      />
    );
  }

  if (!isAuthenticated && !loading) {
    return (
      <>
        <LandingPage 
          onGetStarted={handleGetStarted}
          onSignIn={handleSignIn}
          onAuthSuccess={handleAuthSuccess}
          darkMode={darkMode}
        />
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          mode={authMode}
          onSuccess={handleAuthSuccess}
          darkMode={darkMode}
        />
      </>
    );
  }

  if (loading) {
    return <LoadingSpinner fullScreen darkMode={darkMode} />;
  }

  const formattedFirstName = capitalizeName(currentUser?.firstName);
  const formattedLastName = capitalizeName(currentUser?.lastName);
  const fullName = `${formattedFirstName} ${formattedLastName}`.trim();

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-slate-50 via-white to-slate-100'}`}>
      {/* Header */}
      <header className={`sticky top-0 z-40 transition-colors duration-300 ${darkMode ? 'bg-gray-800/95 backdrop-blur-md border-b border-gray-700' : 'bg-white/80 backdrop-blur-md border-b border-gray-200'}`}>
        <div className="max-w-8xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16 md:h-20 landscape:h-14">
            <div className="flex items-center space-x-2 md:space-x-4">
              <div className="relative group cursor-pointer" onClick={() => setCurrentView('dashboard')}>
                <div 
                  className="w-9 h-9 sm:w-11 sm:h-11 md:w-14 md:h-14 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-105 landscape:w-9 landscape:h-9"
                  style={{ 
                    backgroundColor: darkMode ? '#1F2937' : 'white',
                    border: `2px solid ${redColor}`
                  }}
                >
                  <img 
                    src="/logoupdown.svg" 
                    alt="Logo" 
                    className="h-7 w-7 sm:h-9 sm:w-9 md:h-11 md:w-11 landscape:h-7 landscape:w-7" 
                    loading="eager"
                  />
                </div>
                <div className="absolute -top-1 -right-1 w-2.5 h-2.5 sm:w-3 sm:h-3 md:w-4 md:h-4 rounded-full border-2 border-white animate-pulse landscape:w-2.5 landscape:h-2.5" style={{ backgroundColor: greenColor }}></div>
              </div>
              <div>
                {/* UpDownAlert yazısı büyütüldü */}
                <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight landscape:text-2xl">
                  <span style={{ color: greenColor }}>Up</span>
                  <span style={{ color: redColor }}>Down</span>
                  <span style={{ color: redColor }}>Alert</span>
                </h1>
                <p className={`text-[11px] sm:text-xs font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'} landscape:text-[11px]`}>
                  Welcome back, {formattedFirstName}
                </p>
              </div>
            </div>

            <div className="flex items-center">
              {/* Mobil Menü Butonu */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="sm:hidden landscape:hidden p-2 rounded-xl transition-colors relative z-50"
                style={{ color: darkMode ? 'white' : '#374151' }}
                aria-label="Menu"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>

              {/* Desktop/Tablet/Mobil Yatay Menü - İkonlar ve yazılar büyütüldü */}
              <div className="hidden sm:flex items-center space-x-0.5 lg:space-x-1 landscape:space-x-0.5">
                <nav aria-label="Main navigation" className={`app-nav box-border flex h-fit items-center gap-2 rounded-2xl border-0 p-1 ${darkMode ? 'bg-gray-700/50' : 'bg-white/50'} landscape:gap-1`}>
                  <button
                    onClick={() => setCurrentView('dashboard')}
                    aria-current={currentView === 'dashboard' ? 'page' : undefined}
                    className={`app-nav-item box-border flex flex-col items-center justify-center px-2 lg:px-3 py-1 rounded-xl transition-all duration-200 min-w-[62px] lg:min-w-[72px] ${
                      currentView === 'dashboard' 
                        ? `${darkMode ? 'bg-gray-600 shadow-md' : 'bg-white shadow-md'}`
                        : `${darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`
                    }`}
                    style={currentView === 'dashboard' ? { color: redColor } : {}}
                  >
                    <Activity className="h-5 w-5 lg:h-6 lg:w-6 landscape:h-5 landscape:w-5" />
                    <span className="text-[12px] lg:text-sm font-medium mt-0.5 landscape:text-[12px]">Dashboard</span>
                  </button>
                  <button
                    onClick={() => setCurrentView('synthetic')}
                    aria-current={currentView === 'synthetic' ? 'page' : undefined}
                    className={`app-nav-item box-border flex flex-col items-center justify-center px-2 lg:px-3 py-1 rounded-xl transition-all duration-200 min-w-[62px] lg:min-w-[72px] ${
                      currentView === 'synthetic' 
                        ? `${darkMode ? 'bg-gray-600 shadow-md' : 'bg-white shadow-md'}`
                        : `${darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`
                    }`}
                    style={currentView === 'synthetic' ? { color: greenColor } : {}}
                  >
                    <Play className="h-5 w-5 lg:h-6 lg:w-6 landscape:h-5 landscape:w-5" />
                    <span className="text-[12px] lg:text-sm font-medium mt-0.5 landscape:text-[12px]">Synthetic</span>
                  </button>
                  <button
                    onClick={() => setCurrentView('analytics')}
                    aria-current={currentView === 'analytics' ? 'page' : undefined}
                    className={`app-nav-item box-border flex flex-col items-center justify-center px-2 lg:px-3 py-1 rounded-xl transition-all duration-200 min-w-[62px] lg:min-w-[72px] ${
                      currentView === 'analytics' 
                        ? `${darkMode ? 'bg-gray-600 shadow-md' : 'bg-white shadow-md'}`
                        : `${darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`
                    }`}
                    style={currentView === 'analytics' ? { color: blueColor } : {}}
                  >
                    <BarChart3 className="h-5 w-5 lg:h-6 lg:w-6 landscape:h-5 landscape:w-5" />
                    <span className="text-[12px] lg:text-sm font-medium mt-0.5 landscape:text-[12px]">Analytics</span>
                  </button>
                  <button
                    onClick={handleScanClick}
                    aria-current={currentView === 'scan' ? 'page' : undefined}
                    className={`app-nav-item box-border flex flex-col items-center justify-center px-2 lg:px-3 py-1 rounded-xl transition-all duration-200 min-w-[62px] lg:min-w-[72px] ${
                      currentView === 'scan' 
                        ? `${darkMode ? 'bg-gray-600 shadow-md' : 'bg-white shadow-md'}`
                        : `${darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`
                    }`}
                    style={currentView === 'scan' ? { color: purpleColor } : {}}
                  >
                    <ScanLine className="h-5 w-5 lg:h-6 lg:w-6 landscape:h-5 landscape:w-5" />
                    <span className="text-[12px] lg:text-sm font-medium mt-0.5 landscape:text-[12px]">Scan</span>
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => setCurrentView("admin")}
                      aria-current={currentView === 'admin' ? 'page' : undefined}
                      className={`app-nav-item box-border flex flex-col items-center justify-center px-2 lg:px-3 py-1 rounded-xl transition-all duration-200 min-w-[62px] lg:min-w-[72px] ${
                        currentView === "admin"
                          ? `${darkMode ? 'bg-gray-600 shadow-md' : 'bg-white shadow-md'}`
                          : `${darkMode ? 'text-gray-300 hover:text-white hover:bg-gray-700/50' : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'}`
                      }`}
                      style={currentView === "admin" ? { color: "#EF4444" } : {}}
                    >
                      <Shield className="h-5 w-5 lg:h-6 lg:w-6 landscape:h-5 landscape:w-5" />
                      <span className="text-[12px] lg:text-sm font-medium mt-0.5 landscape:text-[12px]">Admin</span>
                    </button>
                  )}
                </nav>

                <div className="hidden lg:flex items-center space-x-2 landscape:flex landscape:space-x-1">
                  <button
                    onClick={() => setDarkMode(!darkMode)}
                    className={`p-2 rounded-xl transition-all duration-300 landscape:p-1.5 ${
                      darkMode 
                        ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600 hover:scale-110' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 hover:scale-110'
                    }`}
                    title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                  >
                    {darkMode ? <Sun className="h-5 w-5 landscape:h-5 landscape:w-5" /> : <Moon className="h-5 w-5 landscape:h-5 landscape:w-5" />}
                  </button>

                  <div className="relative group">
                    <button className="flex items-center space-x-2 p-2 rounded-xl hover:bg-white/50 dark:hover:bg-gray-700 transition-all duration-200 landscape:p-1.5">
                      <div className="w-7 h-7 rounded-xl flex items-center justify-center landscape:w-6 landscape:h-6" style={{ background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)` }}>
                        <User className="h-3.5 w-3.5 text-white landscape:h-3 landscape:w-3" />
                      </div>
                      <div className="hidden xl:block text-left">
                        <p className={`text-xs font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{formattedFirstName}</p>
                        <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize`}>{currentUser?.planType} Plan</p>
                      </div>
                    </button>
                    <div className={`absolute right-0 top-12 w-48 rounded-2xl shadow-xl border py-2 z-20 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-100'}`}>
                      <button onClick={() => setShowPricingModal(true)} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors rounded-xl mx-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'}`}>
                        <CreditCard className="h-4 w-4" />
                        <span>Billing & Plans</span>
                      </button>
                      <button onClick={() => setShowTelegramModal(true)} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors rounded-xl mx-2 ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-blue-50'}`}>
                        <Send className="h-4 w-4" />
                        <span>Telegram</span>
                      </button>
                      <button onClick={handleLogout} className={`flex items-center space-x-3 px-4 py-3 text-sm w-full text-left transition-colors rounded-xl mx-2 ${darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'}`}>
                        <LogOut className="h-4 w-4" />
                        <span>Sign Out</span>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Refresh ve Add Butonları */}
                <div className="hidden lg:flex items-center space-x-2 landscape:flex landscape:space-x-1">
                  <button
                    onClick={handleRefresh}
                    disabled={refreshing}
                    className={`p-2 rounded-xl transition-all duration-200 disabled:opacity-50 group landscape:p-1.5 ${darkMode ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' : 'text-gray-500 hover:text-gray-700 hover:bg-white/50'}`}
                    title="Refresh"
                  >
                    <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : 'group-hover:rotate-180'} transition-transform duration-500 landscape:h-5 landscape:w-5`} />
                  </button>
                  
                  {currentView === 'synthetic' ? (
                    <button
                      onClick={() => setShowAddSyntheticModal(true)}
                      disabled={syntheticLoading || !canAddSynthetic()}
                      className="px-3 py-2 text-xs font-semibold text-white rounded-xl flex items-center space-x-1 hover:shadow-lg transition disabled:opacity-50 hover:scale-105 landscape:px-2 landscape:py-1.5 landscape:text-[11px]"
                      style={{ background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)` }}
                      title={
                        syntheticLoading 
                          ? 'Loading...'
                          : !canAddSynthetic()
                            ? `Maximum ${currentUser?.maxSyntheticTests} tests allowed`
                            : 'Add Test'
                      }
                    >
                      <Plus className="h-4 w-4 landscape:h-4 landscape:w-4" />
                      <span className="hidden xl:inline landscape:inline">Add Test</span>
                    </button>
                  ) : (
                    <button
                      onClick={() => setShowAddModal(true)}
                      disabled={!canAddWebsite()}
                      className="px-3 py-2 text-xs font-semibold text-white rounded-xl flex items-center space-x-1 hover:shadow-lg transition disabled:opacity-50 hover:scale-105 landscape:px-2 landscape:py-1.5 landscape:text-[11px]"
                      style={{ background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)` }}
                      title="Add Monitor"
                    >
                      <Plus className="h-4 w-4 landscape:h-4 landscape:w-4" />
                      <span className="hidden xl:inline landscape:inline">Add Monitor</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Filtre Çubuğu - aynı kalır (değişiklik yok) */}
      <div className="hidden lg:block landscape:block sticky top-16 lg:top-20 landscape:top-14 z-30 max-w-8xl mx-auto px-4 sm:px-6 lg:px-8 -mt-2 mb-6">
        <div className="flex justify-end">
          <div className={`inline-flex flex-wrap gap-2 p-2 rounded-2xl transition-all duration-300 landscape:gap-1 landscape:p-1.5 ${
            darkMode 
              ? 'bg-gray-800/90 backdrop-blur-sm border border-gray-700' 
              : 'bg-white/90 backdrop-blur-sm border border-gray-100 shadow-lg'
          }`}>
            
            {/* Dashboard View Filtreleri - aynı */}
            {currentView === 'dashboard' && (
              <>
                <button
                  onClick={() => setDashboardFilter('all')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'all'
                      ? 'shadow-md scale-105' 
                      : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'all'
                      ? `linear-gradient(135deg, ${purpleColor}, ${pinkColor})`
                      : darkMode ? '#374151' : '#F3F4F6',
                    color: dashboardFilter === 'all' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <Globe className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">All</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'all'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {websiteTotalCount + blacklistTotalCount}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('online')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'online' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'online' ? greenColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'online' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <Wifi className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Online</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'online'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {upWebsites}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('offline')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'offline' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'offline' ? redColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'offline' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <WifiOff className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Offline</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'offline'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {downWebsites}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('unknown')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'unknown' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'unknown' ? orangeColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'unknown' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <HelpCircle className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Unknown</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'unknown'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {unknownWebsites}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('inactive')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${dashboardFilter === 'inactive' ? 'shadow-md scale-105' : 'hover:scale-105'}`}
                  style={{
                    background: dashboardFilter === 'inactive' ? '#6B7280' : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'inactive' ? 'white' : (darkMode ? '#D1D5DB' : '#374151'),
                  }}
                >
                  <Pause className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Inactive</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${dashboardFilter === 'inactive' ? 'bg-white/20 text-white' : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'}`}>
                    {inactiveWebsites}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('service')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'service' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'service' ? blueColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'service' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <Server className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Services</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'service'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {serviceWebsites}
                  </span>
                </button>

                <button
                  onClick={() => setDashboardFilter('blacklist')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    dashboardFilter === 'blacklist' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: dashboardFilter === 'blacklist' ? '#DC2626' : (darkMode ? '#374151' : '#F3F4F6'),
                    color: dashboardFilter === 'blacklist' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <ShieldAlert className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Blacklist</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    dashboardFilter === 'blacklist'
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {blacklistWebsites}
                  </span>
                </button>
              </>
            )}

            {/* Synthetic View Filtreleri - aynı */}
            {currentView === 'synthetic' && (
              <>
                <button
                  onClick={() => setSyntheticFilter('all')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    syntheticFilter === 'all' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: syntheticFilter === 'all' 
                      ? `linear-gradient(135deg, ${purpleColor}, ${pinkColor})`
                      : darkMode ? '#374151' : '#F3F4F6',
                    color: syntheticFilter === 'all' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <Activity className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">All</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    syntheticFilter === 'all' 
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {syntheticTotal}
                  </span>
                </button>

                <button
                  onClick={() => setSyntheticFilter('success')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    syntheticFilter === 'success' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: syntheticFilter === 'success' ? greenColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: syntheticFilter === 'success' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <CheckCircle className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Success</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    syntheticFilter === 'success' 
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {syntheticSuccess}
                  </span>
                </button>

                <button
                  onClick={() => setSyntheticFilter('failed')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    syntheticFilter === 'failed' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: syntheticFilter === 'failed' ? redColor : (darkMode ? '#374151' : '#F3F4F6'),
                    color: syntheticFilter === 'failed' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <XCircle className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Failed</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    syntheticFilter === 'failed' 
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {syntheticFailed}
                  </span>
                </button>

                <button
                  onClick={() => setSyntheticFilter('inactive')}
                  className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-xl text-xs font-medium transition-all duration-300 landscape:px-2 landscape:py-1 landscape:text-[10px] landscape:space-x-1 ${
                    syntheticFilter === 'inactive' ? 'shadow-md scale-105' : 'hover:scale-105'
                  }`}
                  style={{
                    background: syntheticFilter === 'inactive' ? '#6B7280' : (darkMode ? '#374151' : '#F3F4F6'),
                    color: syntheticFilter === 'inactive' ? 'white' : (darkMode ? '#D1D5DB' : '#374151')
                  }}
                >
                  <Clock className="h-3.5 w-3.5 landscape:h-3 landscape:w-3" />
                  <span className="hidden sm:inline">Inactive</span>
                  <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold landscape:px-1 landscape:text-[9px] ${
                    syntheticFilter === 'inactive' 
                      ? 'bg-white/20 text-white' 
                      : darkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-600'
                  }`}>
                    {syntheticInactive}
                  </span>
                </button>
              </>
            )}

            {/* Analytics View - aynı */}
            {currentView === 'analytics' && (
              <div className="flex items-center gap-2 landscape:gap-1.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center landscape:w-6 landscape:h-6 ${darkMode ? 'bg-blue-900/30' : 'bg-blue-100'}`}>
                  <Cpu className={`h-3.5 w-3.5 landscape:h-3 landscape:w-3 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <span className={`text-xs landscape:text-[10px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  AI Analytics
                </span>
                {!hasAIFeatures && (
                  <button
                    onClick={() => setShowPricingModal(true)}
                    className="px-2 py-1 rounded-lg text-xs font-medium text-white transition-all duration-300 hover:scale-105 landscape:px-1.5 landscape:py-0.5 landscape:text-[9px]"
                    style={{ background: `linear-gradient(135deg, ${purpleColor}, #6D28D9)` }}
                  >
                    Upgrade
                  </button>
                )}
              </div>
            )}

            {/* Scan View - aynı */}
            {currentView === 'scan' && (
              <div className="flex items-center gap-2 landscape:gap-1.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center landscape:w-6 landscape:h-6 ${darkMode ? 'bg-purple-900/30' : 'bg-purple-100'}`}>
                  <Radar className={`h-3.5 w-3.5 landscape:h-3 landscape:w-3 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} />
                </div>
                <span className={`text-xs landscape:text-[10px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Nuclei Scanner
                </span>
                {!isProUser && (
                  <button
                    onClick={() => setShowPricingModal(true)}
                    className="px-2 py-1 rounded-lg text-xs font-medium text-white transition-all duration-300 hover:scale-105 landscape:px-1.5 landscape:py-0.5 landscape:text-[9px]"
                    style={{ background: `linear-gradient(135deg, ${purpleColor}, #6D28D9)` }}
                  >
                    Upgrade
                  </button>
                )}
              </div>
            )}

            {/* Admin View - aynı */}
            {currentView === 'admin' && (
              <div className="flex items-center gap-2 landscape:gap-1.5">
                <div className={`w-7 h-7 rounded-lg flex items-center justify-center landscape:w-6 landscape:h-6 ${darkMode ? 'bg-red-900/30' : 'bg-red-100'}`}>
                  <Shield className={`h-3.5 w-3.5 landscape:h-3 landscape:w-3 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                </div>
                <span className={`text-xs landscape:text-[10px] ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Admin Panel
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Ana İçerik (değişiklik yok) */}
      <main className="w-full px-3 sm:px-4 lg:px-8 pb-12">
        {currentView === 'dashboard' && (
          <Dashboard 
            websites={websites}
            onEditWebsite={setEditingWebsite}
            onDeleteWebsite={handleDeleteWebsite}
            onToggleWebsite={handleToggleWebsite}
            onScanWebsite={handleScanWebsite}
            canActivateWebsite={canActivateWebsite}
            searchTerm={dashboardSearchTerm}
            appliedSearchTerm={dashboardSearchApplied}
            filter={dashboardFilter}
            setSearchTerm={setDashboardSearchTerm}
            onSearchNow={submitDashboardSearch}
            maxIntervalCheck={maxIntervalCheck}
            hasAIFeatures={hasAIFeatures}
            loading={websitesLoading}
            activeCountTotal={activeWebsiteCount + activeBlacklistCount}
            error={error}
            onRetry={() => fetchWebsites({ background: false })}
            user={currentUser}
            darkMode={darkMode}
          />
        )}

        {currentView === 'synthetic' && (
          <SyntheticDashboard
            tests={syntheticTests}
            loading={syntheticLoading}
            error={error}
            onAddClick={() => setShowAddSyntheticModal(true)}
            onEdit={setEditingSynthetic}
            onDelete={handleDeleteSynthetic}
            onRun={handleRunSynthetic}
            onViewResults={setViewingResults}
            onToggleActive={handleToggleSynthetic}
            userLimits={{ maxSyntheticTests: currentUser?.maxSyntheticTests || 1 }}
            filterStatus={syntheticFilter}
            onFilterChange={setSyntheticFilter}
            canAddSynthetic={canAddSynthetic}
            darkMode={darkMode}
          />
        )}

        {currentView === 'analytics' && (
          <Suspense fallback={<LoadingSpinner darkMode={darkMode} />}>
            <AnalyticsDashboard
              key={analyticsRefreshKey}
              currentUser={currentUser}
              darkMode={darkMode}
            />
          </Suspense>
        )}

        {currentView === 'admin' && (
          <Suspense fallback={<LoadingSpinner darkMode={darkMode} />}>
            <Admin
              key={adminRefreshKey}
              darkMode={darkMode}
            />
          </Suspense>
        )}

        {currentView === 'scan' && (
          <Suspense fallback={<LoadingSpinner darkMode={darkMode} />}>
            <ScanDashboard
              key={scanRefreshKey}
              websites={websites}
              user={currentUser}
              initialTarget={scanTarget}
              darkMode={darkMode}
            />
          </Suspense>
        )}
      </main>

      {/* Modals (değişiklik yok) */}
      {showAddModal && (
        <AddWebsiteModal
          onClose={() => setShowAddModal(false)}
          onSubmit={handleAddWebsite}
          onSubmitBlacklist={handleAddBlacklist}
          userLimits={{
            maxPortsPerWebsite: currentUser?.maxPortsPerWebsite || 0,
            maxApisPerWebsite: currentUser?.maxApisPerWebsite || 0,
            maxPingsPerWebsite: currentUser?.maxPingsPerWebsite || 0
          }}
          darkMode={darkMode}
        />
      )}

      {editingWebsite && (
        <EditWebsiteModal
          website={editingWebsite}
          onClose={() => setEditingWebsite(null)}
          onSubmit={handleEditWebsite}
          userLimits={{
            maxPortsPerWebsite: currentUser?.maxPortsPerWebsite || 0,
            maxApisPerWebsite: currentUser?.maxApisPerWebsite || 0,
            maxPingsPerWebsite: currentUser?.maxPingsPerWebsite || 0
          }}
          darkMode={darkMode}
        />
      )}

      {showAddSyntheticModal && (
        <AddSyntheticModal
          onClose={() => setShowAddSyntheticModal(false)}
          onSubmit={handleAddSynthetic}
          userLimits={{
            maxSyntheticTests: currentUser?.maxSyntheticTests || 1
          }}
          darkMode={darkMode}
        />
      )}

      {editingSynthetic && (
        <EditSyntheticModal
          test={editingSynthetic}
          onClose={() => setEditingSynthetic(null)}
          onSubmit={(data) => handleEditSynthetic(editingSynthetic.id, data)}
          darkMode={darkMode}
        />
      )}

      {viewingResults && (
        <SyntheticResultsModal
          test={viewingResults}
          onClose={() => setViewingResults(null)}
          darkMode={darkMode}
        />
      )}

      <PricingModal
        isOpen={showPricingModal}
        onClose={() => setShowPricingModal(false)}
        currentUser={currentUser}
        onPlanChange={handlePlanChange}
        darkMode={darkMode}
      />

      <TelegramSettingsModal
        isOpen={showTelegramModal}
        onClose={() => setShowTelegramModal(false)}
        darkMode={darkMode}
      />

      {/* Scan Pro Modal */}
      {showScanProModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-fadeIn">
          <div className={`rounded-3xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100 animate-slideUp ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className="p-6 text-center">
              <div className="w-20 h-20 mx-auto mb-4 rounded-2xl flex items-center justify-center animate-bounce" style={{ background: `linear-gradient(135deg, ${purpleColor}20, ${purpleColor}40)` }}>
                <ScanLine className="h-10 w-10" style={{ color: purpleColor }} />
              </div>
              <h2 className={`text-2xl font-bold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Professional Feature
              </h2>
              <p className={`mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                Upgrade to <strong className="text-purple-500">Professional</strong> or <strong className="text-purple-500">Enterprise</strong> to unlock advanced security scanning powered by Nuclei.
              </p>
              <div className={`p-4 rounded-xl mb-6 ${darkMode ? 'bg-gray-700/50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Award className="h-4 w-4 text-yellow-500" />
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    Professional Features:
                  </p>
                </div>
                <ul className={`text-sm space-y-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                  <li className="flex items-center gap-2"><Star className="h-3 w-3 text-yellow-500" /> Full Nuclei template access (CVEs, misconfigurations)</li>
                  <li className="flex items-center gap-2"><Star className="h-3 w-3 text-yellow-500" /> Advanced vulnerability detection</li>
                  <li className="flex items-center gap-2"><Star className="h-3 w-3 text-yellow-500" /> Detailed security reports</li>
                  <li className="flex items-center gap-2"><Star className="h-3 w-3 text-yellow-500" /> Continuous monitoring</li>
                </ul>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowScanProModal(false)}
                  className={`flex-1 px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:scale-105 ${
                    darkMode 
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowScanProModal(false);
                    setShowPricingModal(true);
                  }}
                  className="flex-1 px-4 py-2 rounded-xl font-medium text-white transition-all duration-300 hover:scale-105 hover:shadow-xl"
                  style={{ background: `linear-gradient(135deg, ${purpleColor}, #6D28D9)` }}
                >
                  Upgrade Now
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mobil Menü */}
      {mobileMenuOpen && (
        <div className="sm:hidden fixed inset-0 z-50 bg-black/50 backdrop-blur-sm animate-fadeIn" onClick={() => setMobileMenuOpen(false)}>
          <div ref={menuRef} className={`app-mobile-menu absolute right-4 top-16 w-64 rounded-2xl shadow-xl border py-3 px-2 animate-slideRight ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white/95 border-gray-200'}`} onClick={(e) => e.stopPropagation()}>
            <div className="px-3 py-2 border-b border-gray-200/20 mb-2">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 rounded-xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${gradientGreenStart} 0%, ${gradientGreenMiddle} 50%, ${gradientGreenEnd} 100%)` }}>
                  <User className="h-4 w-4 text-white" />
                </div>
                <div>
                  <p className={`font-semibold text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{fullName}</p>
                  <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} capitalize`}>{currentUser?.planType} Plan</p>
                </div>
              </div>
            </div>
            
            <button aria-current={currentView === 'dashboard' ? 'page' : undefined} onClick={() => { setCurrentView('dashboard'); setMobileMenuOpen(false); }} className={`app-nav-item flex items-center space-x-3 w-full px-3 py-3 rounded-xl text-sm transition-all duration-200 ${ currentView === 'dashboard' ? (darkMode ? 'bg-gray-700 shadow-md' : 'bg-white shadow-md') : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50') }`} style={currentView === 'dashboard' ? { color: redColor } : {}}>
              <Activity className="h-4 w-4" /><span>Dashboard</span>
            </button>
            <button aria-current={currentView === 'synthetic' ? 'page' : undefined} onClick={() => { setCurrentView('synthetic'); setMobileMenuOpen(false); }} className={`app-nav-item flex items-center space-x-3 w-full px-3 py-3 rounded-xl text-sm transition-all duration-200 ${ currentView === 'synthetic' ? (darkMode ? 'bg-gray-700 shadow-md' : 'bg-white shadow-md') : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50') }`} style={currentView === 'synthetic' ? { color: greenColor } : {}}>
              <Play className="h-4 w-4" /><span>Synthetic</span>
            </button>
            <button aria-current={currentView === 'analytics' ? 'page' : undefined} onClick={() => { setCurrentView('analytics'); setMobileMenuOpen(false); }} className={`app-nav-item flex items-center space-x-3 w-full px-3 py-3 rounded-xl text-sm transition-all duration-200 ${ currentView === 'analytics' ? (darkMode ? 'bg-gray-700 shadow-md' : 'bg-white shadow-md') : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50') }`} style={currentView === 'analytics' ? { color: blueColor } : {}}>
              <BarChart3 className="h-4 w-4" /><span>Analytics</span>
            </button>
            <button aria-current={currentView === 'scan' ? 'page' : undefined} onClick={handleScanClick} className={`app-nav-item flex items-center space-x-3 w-full px-3 py-3 rounded-xl text-sm transition-all duration-200 ${ currentView === 'scan' ? (darkMode ? 'bg-gray-700 shadow-md' : 'bg-white shadow-md') : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50') }`} style={currentView === 'scan' ? { color: purpleColor } : {}}>
              <ScanLine className="h-4 w-4" /><span>Scan</span>
            </button>
            
            {isAdmin && (
              <button aria-current={currentView === 'admin' ? 'page' : undefined} onClick={() => { setCurrentView("admin"); setMobileMenuOpen(false); }} className={`app-nav-item flex items-center space-x-3 w-full px-3 py-3 rounded-xl text-sm transition-all duration-200 ${ currentView === "admin" ? (darkMode ? 'bg-gray-700 shadow-md' : 'bg-white shadow-md') : (darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50') }`} style={currentView === "admin" ? { color: "#EF4444" } : {}}>
                <Shield className="h-4 w-4" /><span>Admin</span>
              </button>
            )}
            
            <div className="border-t border-gray-200/20 my-2"></div>
            
            {/* Mobil menüde Add butonları */}
            {currentView === 'synthetic' ? (
              <button
                onClick={() => { setShowAddSyntheticModal(true); setMobileMenuOpen(false); }}
                disabled={syntheticLoading || !canAddSynthetic()}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-xl text-sm text-white disabled:opacity-50 transition-all duration-200"
                style={{ backgroundColor: greenColor }}
                title={
                  syntheticLoading
                    ? 'Loading...'
                    : !canAddSynthetic()
                      ? `Maximum ${currentUser?.maxSyntheticTests} tests allowed`
                      : 'Add Test'
                }
              >
                <Plus className="h-4 w-4" />
                <span>Add Test</span>
              </button>
            ) : (
              <button
                onClick={() => { setShowAddModal(true); setMobileMenuOpen(false); }}
                disabled={!canAddWebsite()}
                className="w-full flex items-center space-x-3 px-3 py-2 rounded-xl text-sm text-white disabled:opacity-50 transition-all duration-200"
                style={{ backgroundColor: greenColor }}
                title="Add Monitor"
              >
                <Plus className="h-4 w-4" />
                <span>Add Monitor</span>
              </button>
            )}
            
            <div className="border-t border-gray-200/20 my-2"></div>
            
            <button onClick={() => { setDarkMode(!darkMode); setMobileMenuOpen(false); }} className={`flex items-center space-x-3 w-full px-3 py-2 rounded-xl text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50'}`}>
              {darkMode ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
              <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
            <button onClick={() => { setShowPricingModal(true); setMobileMenuOpen(false); }} className={`flex items-center space-x-3 w-full px-3 py-2 rounded-xl text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50'}`}>
              <CreditCard className="h-4 w-4" /><span>Billing & Plans</span>
            </button>
            <button onClick={() => { setShowTelegramModal(true); setMobileMenuOpen(false); }} className={`flex items-center space-x-3 w-full px-3 py-2 rounded-xl text-sm ${darkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-white/50'}`}>
              <Send className="h-4 w-4" /><span>Telegram</span>
            </button>
            <button onClick={handleLogout} className={`flex items-center space-x-3 w-full px-3 py-2 rounded-xl text-sm ${darkMode ? 'text-red-400 hover:bg-gray-700' : 'text-red-600 hover:bg-red-50'}`}>
              <LogOut className="h-4 w-4" /><span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
